import Konva from 'konva';
import React, { useState, useEffect, useRef } from 'react';
import { Stage, Layer, Rect, Text, Image, Transformer } from 'react-konva';
import styled, { createGlobalStyle, DefaultTheme } from 'styled-components';
import { toPng } from 'html-to-image';
import { FaUndo, FaRedo, FaDownload, FaSave, FaFolderOpen, FaList } from 'react-icons/fa';
import { saveThumbnailTemplate, getAllThumbnailTemplates, getThumbnailTemplateById, deleteThumbnailTemplate, ThumbnailTemplate, processTemplateImages, saveExportedImage, getExportedImages, deleteExportedImage, ExportedImage } from '../../services/thumbnailService';
import { auth } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import Canvas from './components/Canvas';
import LayerPanel from './components/LayerPanel';
import PropertyPanel from './components/PropertyPanel';
import ExportPanel from './components/ExportPanel';
import { KonvaEventObject } from 'konva/lib/Node';
import { Transformer as KonvaTransformer } from 'konva/lib/shapes/Transformer';
import { Text as KonvaText } from 'konva/lib/shapes/Text';
import { Image as KonvaImage } from 'konva/lib/shapes/Image';

// Estilos globales para eliminar scroll
const GlobalStyle = createGlobalStyle`
  html, body {
    margin: 0;
    padding: 0;
    overflow: hidden;
    height: 100%;
  }
  #root {
    height: 100%;
    overflow: hidden;
  }
`;

// Paneles y canvas adaptados al layout global
const GeneratorContainer = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr 240px;
  width: 100%;
  height: 100%;
  margin: 0;
  background:rgb(255, 255, 255);
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
  }
`;

const LeftPanel = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-sizing: border-box;
  background:rgb(255, 255, 255);
  color: #333;
  overflow-y: hidden;
  overflow-x: hidden;
  border-right: 1px solid #e0e0e0;
  padding: 0;
  @media (max-width: 900px) {
    width: 100%;
    height: auto;
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
  }
`;

const RightPanel = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-sizing: border-box;
  background:rgb(255, 255, 255);
  color: #333;
  overflow-y: auto;
  overflow-x: hidden;
  border-left: 1px solid #e0e0e0;
  padding: 0;
  @media (max-width: 900px) {
    width: 100%;
    height: auto;
    border-left: none;
    border-top: 1px solid #e0e0e0;
  }
`;

const CenterPanel = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background:rgb(255, 255, 255);
  box-sizing: border-box;
  overflow: hidden;
  @media (max-width: 900px) {
    width: 100%;
    height: auto;
  }
`;

const CanvasArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: calc(100vh - 120px);
  background:rgb(255, 255, 255);
  overflow: hidden;
  position: relative;
  @media (max-width: 900px) {
    height: 45vh;
    min-height: 200px;
    width: 100%;
  }
`;


const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 5px 10px;
  background:rgb(255, 255, 255);
  width: 100%;
  border-bottom: 1px solid #e0e0e0;
`;


const ToolbarButton = styled.button<{ theme?: DefaultTheme }>`
  background-color: rgb(255, 255, 255);
  color: #333;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;
  font-family: inherit;
  transition: background 0.2s;
  &:hover {
    background-color: #f5f5f5;
    border-color: #ccc;
  }
  &:disabled {
    background-color:rgb(255, 255, 255);
    color: #aaa;
    cursor: not-allowed;
  }
`;


// Tipos de capa
interface BaseLayer {
  id: string;
  name: string;
  type: string;
  x?: number;
  y?: number;
  draggable?: boolean;
}

interface TextLayer extends BaseLayer {
  type: 'text';
  text: string;
  fontSize: number;
  fontFamily: string;
  fill: string;
  width: number;
  textAlign: string;
  height?: number;
}

interface ImageLayer extends BaseLayer {
  type: 'image';
  width: number;
  height: number;
  opacity: number;
  imageUrl: string;
}

interface BackgroundLayer extends BaseLayer {
  type: 'background';
  fill: string;
}

type Layer = TextLayer | ImageLayer | BackgroundLayer;

// Componente para texto editable
interface TextNodeProps {
  shapeProps: TextLayer;
  isSelected: boolean;
  onSelect: (e: KonvaEventObject<MouseEvent>) => void;
  onChange: (newProps: Partial<TextLayer>) => void;
}
const TextNode: React.FC<TextNodeProps> = ({ shapeProps, isSelected, onSelect, onChange }) => {
  const shapeRef = useRef<KonvaText>(null);
  const trRef = useRef<KonvaTransformer>(null);

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  return (
    <>
      <Text
        ref={shapeRef}
        {...shapeProps}
        draggable
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={(e) => {
          onChange({
            ...shapeProps,
            x: e.target.x(),
            y: e.target.y(),
          });
        }}
        onTransformEnd={(e) => {
          // Transformer cambia escala, necesitamos ajustar tamaño de fuente
          const node = shapeRef.current;
          if (!node) return;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();

          // Resetear escala y ajustar tamaño de fuente
          node.scaleX(1);
          node.scaleY(1);
          
          onChange({
            ...shapeProps,
            x: node.x(),
            y: node.y(),
            fontSize: Math.round(shapeProps.fontSize * scaleX),
            width: Math.max(5, node.width() * scaleX),
            height: Math.max(5, node.height() * scaleY),
          });
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            // Limitar tamaño mínimo
            if (newBox.width < 5 || newBox.height < 5) {
              return oldBox;
            }
            return newBox;
          }}
        />
      )}
    </>
  );
};

// Componente para imagen
interface ImageNodeProps {
  shapeProps: ImageLayer;
  isSelected: boolean;
  onSelect: (e: KonvaEventObject<MouseEvent>) => void;
  onChange: (newProps: Partial<ImageLayer>) => void;
  image: HTMLImageElement | undefined;
}
const ImageNode: React.FC<ImageNodeProps> = ({ shapeProps, isSelected, onSelect, onChange, image }) => {
  const shapeRef = useRef<KonvaImage>(null);
  const trRef = useRef<KonvaTransformer>(null);

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  return (
    <>
      <Image
        ref={shapeRef}
        {...shapeProps}
        image={image}
        draggable
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={(e) => {
          onChange({
            ...shapeProps,
            x: e.target.x(),
            y: e.target.y(),
          });
        }}
        onTransformEnd={(e) => {
          if (shapeRef.current) {
            const node = shapeRef.current;
            const scaleX = node.scaleX();
            const scaleY = node.scaleY();

            // Resetear escala
            node.scaleX(1);
            node.scaleY(1);
            
            onChange({
              ...shapeProps,
              x: node.x(),
              y: node.y(),
              width: Math.max(5, node.width() * scaleX),
              height: Math.max(5, node.height() * scaleY),
            });
          }
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            // Limitar tamaño mínimo
            if (newBox.width < 5 || newBox.height < 5) {
              return oldBox;
            }
            return newBox;
          }}
        />
      )}
    </>
  );
};

// Fuentes disponibles
const availableFonts = [
  'Montserrat',
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Courier New',
  'Georgia',
  'Verdana',
  'Impact'
];

// Presets de texto predefinidos
const textPresets = [
  {
    name: 'Título Principal',
    fontFamily: 'Montserrat',
    fontSize: 60,
    fill: '#FFFFFF',
    textAlign: 'center'
  },
  {
    name: 'Subtítulo',
    fontFamily: 'Montserrat',
    fontSize: 40,
    fill: '#FFFFFF',
    textAlign: 'center'
  },
  {
    name: 'Destacado',
    fontFamily: 'Impact',
    fontSize: 50,
    fill: '#FF0000',
    textAlign: 'center'
  },
  {
    name: 'Pequeño',
    fontFamily: 'Montserrat',
    fontSize: 24,
    fill: '#FFFFFF',
    textAlign: 'left'
  }
];


function App() {
  // Estado para el canvas
  const stageRef = useRef<Konva.Stage>(null);
  const canvasAreaRef = useRef<HTMLDivElement>(null);
  const [canvasScale, setCanvasScale] = useState(1);

  // Escalado responsivo para mantener 16:9 y que quepa en el área visible
  useEffect(() => {
    function handleResize() {
      if (!canvasAreaRef.current) return;
      const parent = canvasAreaRef.current;
      // Espacio disponible en el área central
      const availableWidth = parent.offsetWidth;
      const availableHeight = parent.offsetHeight;
      // 1280x720 relación 16:9
      const scale = Math.min(
        availableWidth / 1280,
        availableHeight / 720,
        1 // nunca escalar por encima de 1
      );
      setCanvasScale(scale);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Estado para las capas
  const [layers, setLayers] = useState<Layer[]>([]);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [selectedObject, setSelectedObject] = useState<Layer | null>(null);
  
  // Estado para el historial de acciones (deshacer/rehacer)
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Estado para el color de fondo
  const [backgroundColor, setBackgroundColor] = useState('#000000');
  
  // Estado para las imágenes cargadas
  const [images, setImages] = useState<Record<string, HTMLImageElement | undefined>>({});
  
  // Dimensiones del canvas (miniatura de YouTube: 1280x720)
  const canvasWidth = 1280;
  const canvasHeight = 720;
  
  // Inicializar con capa de fondo
  useEffect(() => {
    // Crear capa de fondo
    const backgroundLayer: BackgroundLayer = {
      id: 'background',
      name: 'Fondo',
      type: 'background',
      fill: backgroundColor
    };
    
    setLayers([backgroundLayer]);
    saveToHistory();
  }, []);
  
  // Función para guardar el estado actual en el historial
  const saveToHistory = () => {
    // Guardar el estado actual
    const currentState = JSON.stringify(layers);
    
    // Si estamos en medio del historial, eliminar los estados futuros
    if (historyIndex < history.length - 1) {
      setHistory(history.slice(0, historyIndex + 1));
    }
    
    // Añadir el nuevo estado al historial
    setHistory([...history.slice(0, historyIndex + 1), currentState]);
    setHistoryIndex(historyIndex + 1);
  };
  
  // Función para deshacer
  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const prevState = history[newIndex];
      
      setLayers(JSON.parse(prevState) as Layer[]);
      setHistoryIndex(newIndex);
    }
  };
  
  // Función para rehacer
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      
      setLayers(JSON.parse(nextState) as Layer[]);
      setHistoryIndex(newIndex);
    }
  };
  
  // Manejar la selección de objetos en el canvas
  const handleObjectSelect = (obj: Layer | null) => {
    if (!obj) {
      setSelectedLayerId(null);
      setSelectedObject(null);
      return;
    }
    
    setSelectedLayerId(obj.id);
    setSelectedObject(layers.find(layer => layer.id === obj.id) ?? null);
  };
  
  // Seleccionar una capa
  const handleSelectLayer = (layerId: string) => {
    setSelectedLayerId(layerId);
    setSelectedObject(layers.find(layer => layer.id === layerId) ?? null);
  };
  
  // Mover una capa hacia arriba
  const handleMoveLayerUp = (layerId: string) => {
    if (layerId === 'background') return; // No mover el fondo
    
    const index = layers.findIndex(layer => layer.id === layerId);
    if (index <= 1) return; // Ya está en la parte superior o justo después del fondo
    
    const newLayers = [...layers];
    const temp = newLayers[index];
    newLayers[index] = newLayers[index - 1];
    newLayers[index - 1] = temp;
    
    setLayers(newLayers);
    saveToHistory();
  };
  
  // Mover una capa hacia abajo
  const handleMoveLayerDown = (layerId: string) => {
    if (layerId === 'background') return; // No mover el fondo
    
    const index = layers.findIndex(layer => layer.id === layerId);
    if (index >= layers.length - 1 || index === 0) return; // Ya está en la parte inferior o es el fondo
    
    const newLayers = [...layers];
    const temp = newLayers[index];
    newLayers[index] = newLayers[index + 1];
    newLayers[index + 1] = temp;
    
    setLayers(newLayers);
    saveToHistory();
  };
  
  // Eliminar una capa
  const handleDeleteLayer = (layerId: string) => {
    if (layerId === 'background') return; // No eliminar el fondo
    
    const newLayers = layers.filter(layer => layer.id !== layerId);
    setLayers(newLayers);
    
    if (selectedLayerId === layerId) {
      setSelectedLayerId(null);
      setSelectedObject(null);
    }
    
    saveToHistory();
  };
  
  // Duplicar una capa
  const handleDuplicateLayer = (layerId: string) => {
    if (layerId === 'background') return; // No duplicar el fondo
    
    const layerToDuplicate = layers.find(layer => layer.id === layerId);
    if (!layerToDuplicate) return;
    
    const newLayer: Layer = {
      ...layerToDuplicate,
      id: `${layerToDuplicate.type}_${Date.now()}`,
      name: `${layerToDuplicate.name} (copia)`,
      x: (layerToDuplicate.x ?? 0) + 20,
      y: (layerToDuplicate.y ?? 0) + 20,
      type: layerToDuplicate.type // preserve type as literal
    } as Layer;
    
    setLayers([...layers, newLayer]);
    setSelectedLayerId(newLayer.id);
    setSelectedObject(newLayer);
    saveToHistory();
  };
  
  // Añadir una capa de texto
  const handleAddTextLayer = () => {
    const id = `text_${Date.now()}`;
    const newTextLayer: TextLayer = {
      id,
      name: 'Texto',
      type: 'text',
      x: canvasWidth / 2 - 100,
      y: canvasHeight / 2 - 20,
      text: 'Nuevo Texto',
      fontSize: 40,
      fontFamily: 'Montserrat',
      fill: '#FFFFFF',
      width: 200,
      textAlign: 'center',
      draggable: true
    };
    
    setLayers([...layers, newTextLayer]);
    setSelectedLayerId(id);
    setSelectedObject(newTextLayer);
    saveToHistory();
  };
  
  // Añadir una capa de imagen
  interface TextPreset {
  name: string;
  fontFamily: string;
  fontSize: number;
  fill: string;
  textAlign: string;
}

const handleAddImageLayer = () => {
    // Crear un input de archivo oculto
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        const file = target.files[0];
        const reader = new FileReader();
        
        reader.onload = (event) => {
          const id = `image_${Date.now()}`;
          
          // Crear una imagen para obtener dimensiones
          const img = new window.Image();
          const result = event.target?.result;
          if (typeof result !== 'string') return;
          img.src = result;
          
          img.onload = () => {
            // Calcular dimensiones manteniendo proporciones
            let width = img.width;
            let height = img.height;
            const maxDim = 300;
            
            if (width > maxDim || height > maxDim) {
              if (width > height) {
                height = (height / width) * maxDim;
                width = maxDim;
              } else {
                width = (width / height) * maxDim;
                height = maxDim;
              }
            }
            
            // Crear nueva capa de imagen
            const newImageLayer: ImageLayer = {
              id,
              name: 'Imagen',
              type: 'image',
              x: canvasWidth / 2 - width / 2,
              y: canvasHeight / 2 - height / 2,
              width,
              height,
              opacity: 1,
              draggable: true,
              imageUrl: result
            };
            
            // Guardar referencia a la imagen
            setImages(prev => ({
              ...prev,
              [id]: img
            }));
            
            setLayers([...layers, newImageLayer]);
            setSelectedLayerId(id);
            setSelectedObject(newImageLayer);
            saveToHistory();
          };
        };
        
        reader.readAsDataURL(file);
      }
    };
    
    fileInput.click();
  };
  
  // Actualizar texto
  const handleUpdateText = (text: string) => {
    if (!selectedLayerId || !selectedObject || selectedObject.type !== 'text') return;
    
    const updatedLayers = layers.map(layer => 
      layer.id === selectedLayerId ? { ...layer, text } : layer
    );
    
    setLayers(updatedLayers);
    setSelectedObject({ ...selectedObject, text } as TextLayer);
    saveToHistory();
  };
  
  // Actualizar familia de fuente
  const handleUpdateFontFamily = (fontFamily: string) => {
    if (!selectedLayerId || !selectedObject || selectedObject.type !== 'text') return;
    
    const updatedLayers = layers.map(layer => 
      layer.id === selectedLayerId ? { ...layer, fontFamily } : layer
    );
    
    setLayers(updatedLayers);
    setSelectedObject({ ...selectedObject, fontFamily } as TextLayer);
    saveToHistory();
  };
  
  // Actualizar tamaño de fuente
  const handleUpdateFontSize = (fontSize: number) => {
    if (!selectedLayerId || !selectedObject || selectedObject.type !== 'text') return;
    
    const updatedLayers = layers.map(layer => 
      layer.id === selectedLayerId ? { ...layer, fontSize } : layer
    );
    
    setLayers(updatedLayers);
    setSelectedObject({ ...selectedObject, fontSize } as TextLayer);
    saveToHistory();
  };
  
  // Actualizar alineación de texto
  const handleUpdateTextAlign = (textAlign: string) => {
    if (!selectedLayerId || !selectedObject || selectedObject.type !== 'text') return;
    
    const updatedLayers = layers.map(layer => 
      layer.id === selectedLayerId ? { ...layer, textAlign } : layer
    );
    
    setLayers(updatedLayers);
    setSelectedObject({ ...selectedObject, textAlign } as TextLayer);
    saveToHistory();
  };
  
  // Actualizar color
  const handleUpdateColor = (fill: string) => {
    if (!selectedLayerId) return;
    
    const updatedLayers = layers.map(layer => 
      layer.id === selectedLayerId ? { ...layer, fill } : layer
    );
    
    setLayers(updatedLayers);
    if (!selectedObject) return;
    if (selectedObject.type === 'text') {
      setSelectedObject({ ...selectedObject, fill } as TextLayer);
    } else if (selectedObject.type === 'background') {
      setSelectedObject({ ...selectedObject, fill } as BackgroundLayer);
    } else if (selectedObject.type === 'image') {
      setSelectedObject({ ...selectedObject } as ImageLayer);
    }
    saveToHistory();
  };
  
  // Actualizar opacidad
  const handleUpdateOpacity = (opacity: number) => {
    if (!selectedLayerId || !selectedObject || selectedObject.type !== 'image') return;
    
    const updatedLayers = layers.map(layer => 
      layer.id === selectedLayerId ? { ...layer, opacity } : layer
    );
    
    setLayers(updatedLayers);
    setSelectedObject({ ...selectedObject, opacity } as ImageLayer);
    saveToHistory();
  };
  
  // Actualizar color de fondo
  const handleUpdateBackgroundColor = (fill: string) => {
    setBackgroundColor(fill);
    
    const updatedLayers = layers.map(layer => 
      layer.id === 'background' ? { ...layer, fill } : layer
    );
    
    setLayers(updatedLayers);
    
    if (selectedLayerId === 'background' && selectedObject) {
      setSelectedObject({ ...selectedObject, fill } as BackgroundLayer);
    }
    
    saveToHistory();
  };
  
  // Aplicar preset de texto
  const handleApplyTextPreset = (preset: TextPreset) => {
    if (!selectedLayerId || !selectedObject || selectedObject.type !== 'text') return;
    
    const updatedLayers = layers.map(layer => 
      layer.id === selectedLayerId ? { 
        ...layer, 
        fontFamily: preset.fontFamily,
        fontSize: preset.fontSize,
        fill: preset.fill,
        textAlign: preset.textAlign
      } : layer
    );
    
    setLayers(updatedLayers);
    setSelectedObject({ 
      ...selectedObject, 
      fontFamily: preset.fontFamily,
      fontSize: preset.fontSize,
      fill: preset.fill,
      textAlign: preset.textAlign
    } as TextLayer);
    
    saveToHistory();
  };
  
  // Actualizar posición y tamaño de un objeto
  const handleObjectChange = (id: string, newProps: Partial<Layer>) => {
    const updatedLayers = layers.map(layer => 
      layer.id === id ? { ...layer, ...newProps } : layer
    );
    setLayers(updatedLayers as Layer[]);
    if (selectedLayerId === id && selectedObject) {
      // Use type guards to cast correctly
      if (selectedObject.type === 'text') {
        setSelectedObject({ ...selectedObject, ...newProps } as TextLayer);
      } else if (selectedObject.type === 'image') {
        setSelectedObject({ ...selectedObject, ...newProps } as ImageLayer);
      } else if (selectedObject.type === 'background') {
        setSelectedObject({ ...selectedObject, ...newProps } as BackgroundLayer);
      }
    }
    saveToHistory();
  };
  
  // Exportar imagen y guardarla en Firebase
  const handleExportImage = async () => {
    if (!stageRef.current) return;
    
    setIsLoading(true);
    try {
      // Generar la imagen
      const dataUrl = stageRef.current.toDataURL({ pixelRatio: 2 });
      
      // Guardar en Firebase
      const userId = auth.currentUser?.uid;
      if (!userId) {
        alert('Debes iniciar sesión para guardar imágenes');
        return;
      }
      
      // Guardar la imagen en Firebase
      const imageName = `Miniatura ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
      await saveExportedImage(dataUrl, userId, imageName);
      
      // Descargar la imagen localmente
      const link = document.createElement('a');
      link.download = 'miniatura-youtube.jpg';
      link.href = dataUrl;
      link.click();
      
      alert('Imagen guardada correctamente en la nube y descargada localmente');
    } catch (error) {
      console.error('Error al exportar la imagen:', error);
      alert('Error al guardar la imagen');
    } finally {
      setIsLoading(false);
    }
  };
  

  
  // Estado para los modales
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templates, setTemplates] = useState<ThumbnailTemplate[]>([]);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showExportedImagesModal, setShowExportedImagesModal] = useState(false);
  const [exportedImages, setExportedImages] = useState<ExportedImage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Cargar plantillas al iniciar
  useEffect(() => {
    loadTemplates();
  }, []);

  // Función para eliminar una plantilla
  const handleDeleteTemplate = async (templateId: string) => {
    if (!templateId) return;
    
    if (window.confirm('¿Estás seguro de que deseas eliminar esta plantilla? Esta acción no se puede deshacer.')) {
      setIsLoading(true);
      try {
        await deleteThumbnailTemplate(templateId);
        // Recargar la lista de plantillas después de eliminar
        loadTemplates();
        alert('Plantilla eliminada correctamente');
      } catch (error) {
        console.error('Error al eliminar la plantilla:', error);
        alert('Error al eliminar la plantilla');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Cargar imágenes exportadas
  const loadExportedImages = async () => {
    setIsLoading(true);
    try {
      const userId = auth.currentUser?.uid;
      if (userId) {
        const images = await getExportedImages(userId);
        setExportedImages(images);
      }
    } catch (error) {
      console.error('Error al cargar las imágenes exportadas:', error);
      alert('Error al cargar las imágenes guardadas');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Eliminar una imagen exportada
  const handleDeleteExportedImage = async (imageId: string) => {
    if (!imageId) return;
    
    if (window.confirm('¿Estás seguro de que deseas eliminar esta imagen? Esta acción no se puede deshacer.')) {
      setIsLoading(true);
      try {
        await deleteExportedImage(imageId);
        // Recargar la lista de imágenes después de eliminar
        loadExportedImages();
        alert('Imagen eliminada correctamente');
      } catch (error) {
        console.error('Error al eliminar la imagen:', error);
        alert('Error al eliminar la imagen');
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  // Abrir modal para ver imágenes exportadas
  const handleViewExportedImages = () => {
    setShowExportedImagesModal(true);
    loadExportedImages();
  };
  
  // Cargar plantillas desde Firebase
  const loadTemplates = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (userId) {
        const loadedTemplates = await getAllThumbnailTemplates(userId);
        setTemplates(loadedTemplates);
      }
    } catch (error) {
      console.error('Error al cargar plantillas:', error);
    }
  };

  // Abrir modal para guardar proyecto
  const handleSaveProject = () => {
    setShowSaveModal(true);
  };

  // Guardar proyecto en Firebase
  const saveProjectToFirebase = async () => {
    if (!templateName.trim()) {
      alert('Por favor, introduce un nombre para la plantilla');
      return;
    }

    setIsLoading(true);
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        alert('Debes iniciar sesión para guardar plantillas');
        setIsLoading(false);
        return;
      }

      // Crear la plantilla inicial
      const template: ThumbnailTemplate = {
        name: templateName,
        layers,
        backgroundColor,
        userId
      };

      // Procesar y subir las imágenes a Firebase Storage
      const processedTemplate = await processTemplateImages(template);
      
      // Guardar la plantilla con URLs remotas
      await saveThumbnailTemplate(processedTemplate);
      
      setShowSaveModal(false);
      setTemplateName('');
      alert('Plantilla guardada correctamente');
      
      // Recargar la lista de plantillas
      await loadTemplates();
    } catch (error) {
      console.error('Error al guardar la plantilla:', error);
      alert('Error al guardar la plantilla: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Abrir modal para cargar plantillas
  const handleLoadProject = () => {
    setShowTemplatesModal(true);
  };

  // Cargar una plantilla específica
  const loadTemplate = async (template: ThumbnailTemplate) => {
    setIsLoading(true);
    try {
      // Establecer el color de fondo
      setBackgroundColor(template.backgroundColor);
      
      // Cargar las capas
      setLayers(template.layers);
      
      // Cargar imágenes EXACTAMENTE como antes, pero con crossOrigin
      template.layers.forEach((layer: Layer) => {
        if (layer.type === 'image' && layer.imageUrl) {
          const img = new window.Image();
          img.crossOrigin = 'anonymous'; // Solo añadimos esto
          img.onload = () => {
            setImages(prev => ({
              ...prev,
              [layer.id]: img
            }));
            console.log(`Imagen cargada: ${layer.id}`);
          };
          img.onerror = (error) => {
            console.error(`Error al cargar imagen ${layer.id}:`, error);
          };
          img.src = layer.imageUrl;
        }
      });
      
      // Resetear la selección
      setSelectedLayerId(null);
      setSelectedObject(null);
      
      // Guardar en el historial
      saveToHistory();
      
      // Cerrar el modal
      setShowTemplatesModal(false);
      
    } catch (error) {
      console.error('Error al cargar la plantilla:', error);
      alert('Error al cargar la plantilla');
    } finally {
      setIsLoading(false);
    }
  };

  // Guardar proyecto
  const handleSaveProjectLocal = () => {
    // Guardar el estado actual del proyecto
    const projectData = {
      layers,
      backgroundColor
    };
    
    // Guardar en localStorage
    localStorage.setItem('thumbnailProject', JSON.stringify(projectData));
    alert('Proyecto guardado como predeterminado');
  };

  // Cargar proyecto guardado
  const handleLoadProjectLocal = () => {
    const savedProject = localStorage.getItem('thumbnailProject');
    if (savedProject) {
      const projectData = JSON.parse(savedProject);
      
      // Establecer el color de fondo
      setBackgroundColor(projectData.backgroundColor);
      
      // Cargar las capas
      setLayers(projectData.layers);
      
      // Cargar imágenes si hay capas de imagen
      projectData.layers.forEach((layer: Layer) => {
        if (layer.type === 'image' && layer.imageUrl) {
          const img = new window.Image();
          img.src = layer.imageUrl;
          img.onload = () => {
            setImages(prev => ({
              ...prev,
              [layer.id]: img
            }));
          };
        }
      });
      
      saveToHistory();
    }
  };

  // Cargar plantillas al iniciar
  useEffect(() => {
    loadTemplates();
  }, []);
  return (
    <>
      <GlobalStyle />
      <GeneratorContainer>
      <LeftPanel>
        <LayerPanel
          layers={layers}
          selectedLayerId={selectedLayerId}
          onSelectLayer={handleSelectLayer}
          onMoveLayerUp={handleMoveLayerUp}
          onMoveLayerDown={handleMoveLayerDown}
          onDeleteLayer={handleDeleteLayer}
          onDuplicateLayer={handleDuplicateLayer}
          onAddTextLayer={handleAddTextLayer}
          onAddImageLayer={handleAddImageLayer}
        />
      </LeftPanel>
      
      <CenterPanel>
        <Toolbar>
          <div style={{ display: 'flex', gap: '10px' }}>
            <ToolbarButton 
              onClick={handleUndo} 
              disabled={historyIndex <= 0}
            >
              <FaUndo /> Deshacer
            </ToolbarButton>
            <ToolbarButton 
              onClick={handleRedo} 
              disabled={historyIndex >= history.length - 1}
            >
              <FaRedo /> Rehacer
            </ToolbarButton>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <ToolbarButton 
              onClick={handleLoadProject}
              style={{ backgroundColor: '#f39c12', color: 'white' }}
            >
              <FaList /> Plantillas
            </ToolbarButton>
            <ToolbarButton 
              onClick={handleExportImage}
              style={{ backgroundColor: '#4a90e2', color: 'white' }}
              disabled={isLoading}
            >
              <FaDownload /> {isLoading ? 'Guardando...' : 'Exportar JPG'}
            </ToolbarButton>
            <ToolbarButton 
              onClick={handleViewExportedImages}
              style={{ backgroundColor: '#3498db', color: 'white' }}
            >
              <FaFolderOpen /> Mis Imágenes
            </ToolbarButton>
            <ToolbarButton 
              onClick={handleSaveProject}
              style={{ backgroundColor: '#27ae60', color: 'white' }}
            >
              <FaSave /> Guardar
            </ToolbarButton>
          </div>
        </Toolbar>

        {/* Responsive canvas scaling wrapper */}
        <CanvasArea ref={canvasAreaRef}>
          <div
            style={{
              width: canvasWidth,
              height: canvasHeight,
              transform: `scale(${canvasScale})`,
              transformOrigin: 'center center',
              display: 'inline-block',
              boxShadow: '0 0 10px rgba(0,0,0,0.3)',
              background: '#ffffff',
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginLeft: `-${canvasWidth/2}px`,
              marginTop: `-${canvasHeight/2}px`,
            }}
          >
            <Stage
              width={canvasWidth}
              height={canvasHeight}
              ref={stageRef}
              onClick={(e) => {
                // Deseleccionar cuando se hace clic en el fondo
                if (e.target === e.target.getStage()) {
                  handleObjectSelect(null);
                }
              }}
            >
            {/* Capa de fondo */}
            <Layer>
              <Rect
                x={0}
                y={0}
                width={canvasWidth}
                height={canvasHeight}
                fill={backgroundColor}
                onClick={() => handleObjectSelect(layers[0])}
                stroke="#e0e0e0"
                strokeWidth={1}
              />
            </Layer>
            
            {/* Capas de contenido */}
            <Layer>
              {layers.map((layer, i) => {
                if (i === 0) return null; // Saltar la capa de fondo
                
                if (layer.type === 'text') {
                  return (
                    <TextNode
                      key={layer.id}
                      shapeProps={layer}
                      isSelected={layer.id === selectedLayerId}
                      onSelect={() => handleObjectSelect(layer)}
                      onChange={(newAttrs) => handleObjectChange(layer.id, newAttrs)}
                    />
                  );
                } else if (layer.type === 'image') {
                  return (
                    <ImageNode
                      key={layer.id}
                      shapeProps={layer}
                      image={images[layer.id]}
                      isSelected={layer.id === selectedLayerId}
                      onSelect={() => handleObjectSelect(layer)}
                      onChange={(newAttrs) => handleObjectChange(layer.id, newAttrs)}
                    />
                  );
                }
                
                return null;
              })}
            </Layer>
          </Stage>
          </div>
        </CanvasArea>
      </CenterPanel>
      
      <RightPanel>
        <PropertyPanel
          selectedObject={selectedObject}
          onUpdateText={handleUpdateText}
          onUpdateFontFamily={handleUpdateFontFamily}
          onUpdateFontSize={handleUpdateFontSize}
          onUpdateTextAlign={handleUpdateTextAlign}
          onUpdateColor={handleUpdateColor}
          onUpdateOpacity={handleUpdateOpacity}
          onUpdateBackgroundColor={handleUpdateBackgroundColor}
          onApplyTextPreset={handleApplyTextPreset}
          availableFonts={availableFonts}
          textPresets={textPresets}
        />
      </RightPanel>
    </GeneratorContainer>

      {/* Modal para guardar plantilla */}
      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '5px',
            width: '400px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}>
            <h3>Guardar Plantilla</h3>
            <div style={{ marginBottom: '15px' }}>
              <label htmlFor="templateName" style={{ display: 'block', marginBottom: '5px' }}>Nombre de la plantilla:</label>
              <input
                id="templateName"
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
                placeholder="Mi plantilla"
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowSaveModal(false)}
                style={{ padding: '8px 15px', backgroundColor: '#e0e0e0', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button
                onClick={saveProjectToFirebase}
                disabled={isLoading}
                style={{ padding: '8px 15px', backgroundColor: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                {isLoading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para cargar plantillas */}
      {showTemplatesModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '5px',
            width: '500px',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}>
            <h3>Mis Plantillas</h3>
            {isLoading ? (
              <p>Cargando plantillas...</p>
            ) : templates.length === 0 ? (
              <p>No tienes plantillas guardadas.</p>
            ) : (
              <div style={{ marginTop: '15px' }}>
                {templates.map((template) => (
                  <div key={template.id} style={{
                    padding: '10px',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <strong>{template.name}</strong>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {template.createdAt ? new Date((template.createdAt as any).seconds * 1000).toLocaleDateString() : 'Fecha desconocida'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        onClick={() => loadTemplate(template)}
                        style={{ padding: '5px 10px', backgroundColor: '#f39c12', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Cargar
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id!)}
                        style={{ padding: '5px 10px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowTemplatesModal(false)}
                style={{ padding: '8px 15px', backgroundColor: '#e0e0e0', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal para ver imágenes exportadas */}
      {showExportedImagesModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '5px',
            width: '800px',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}>
            <h3>Mis Imágenes Guardadas</h3>
            <p style={{ marginBottom: '15px' }}>
              Aquí puedes ver todas las imágenes que has exportado y guardado en la nube. Puedes descargarlas o eliminarlas.
            </p>
            {isLoading ? (
              <p>Cargando imágenes...</p>
            ) : exportedImages.length === 0 ? (
              <p>No tienes imágenes guardadas.</p>
            ) : (
              <div style={{ marginTop: '15px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '15px' }}>
                {exportedImages.map((image) => (
                  <div key={image.id} style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '5px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ height: '150px', overflow: 'hidden', position: 'relative' }}>
                      <img 
                        src={image.imageUrl} 
                        alt={image.name} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                      />
                    </div>
                    <div style={{ padding: '10px' }}>
                      <div style={{ marginBottom: '5px', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {image.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                        {image.createdAt ? new Date((image.createdAt as any).seconds * 1000).toLocaleDateString() : 'Fecha desconocida'}
                      </div>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <a
                          href={image.imageUrl}
                          download={image.name}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ flex: 1, padding: '5px 0', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', textAlign: 'center', textDecoration: 'none', fontSize: '12px' }}
                        >
                          Descargar
                        </a>
                        <button
                          onClick={() => handleDeleteExportedImage(image.id!)}
                          style={{ flex: 1, padding: '5px 0', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowExportedImagesModal(false)}
                style={{ padding: '8px 15px', backgroundColor: '#e0e0e0', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const GeneradorMiniaturas: React.FC = () => {
  const { hasPermission } = useAuth();
  
  // Verificar si el usuario tiene permiso para acceder a esta página
  if (!hasPermission('view:thumbnails_generator')) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        textAlign: 'center',
        padding: '20px'
      }}>
        <h2>Acceso Restringido</h2>
        <p>No tienes permisos para acceder al Generador de Miniaturas.</p>
        <p>Esta herramienta está disponible solo para administradores y usuarios autorizados.</p>
        <a href="/" style={{
          marginTop: '20px',
          padding: '10px 20px',
          background: '#1976d2',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '4px'
        }}>Volver al Inicio</a>
      </div>
    );
  }
  
  return <App />;
};

export default GeneradorMiniaturas;
